/**
 * EventPosterPill — quiet host-only affordance for composing the
 * Full Event Poster (a printable, frameable artifact for the whole
 * night, not a contact sheet).
 *
 * Design constraints (matching the rest of the EventPage pill family):
 *   • glass-dark + gold-border italic-serif — never a bright CTA
 *   • copy locked to "Compose the event poster ✦"
 *   • tap opens a soft modal with:
 *       - a 1-line italic explanation of what the poster is
 *       - an optional subtitle input (italic, ≤80 chars)
 *       - an optional cover inscription input (≤80 chars) that
 *         defaults to "auto" — auto-propagates the latest chapter's
 *         host_note so the emotional spine of the event stays
 *         consistent across artifacts
 *       - a primary "Compose" button + Close
 *   • after compose: shows a parchment-framed preview of the PNG +
 *     "Save for printing ✦" download link, "Save for sharing"
 *     PDF link, and a quiet "Compose another" affordance
 *   • whisper-style brandToast on success
 *
 * The poster requires >=3 memories on the wall — the backend enforces
 * this with a 400. We surface a soft inline error if we hit it.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { X, DownloadSimple, Sparkle, FilePdf } from '@phosphor-icons/react';
import { brandToast } from '../lib/brandToast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const absoluteMedia = (u) => (u && !u.startsWith('http') ? `${BACKEND_URL}${u}` : u);

export default function EventPosterPill({ event }) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState('idle');   // idle | composing | ready | error
  const [subtitle, setSubtitle] = useState('');
  const [hostNote, setHostNote] = useState('');
  const [poster, setPoster] = useState(null);
  const [error, setError] = useState(null);

  const handleCompose = async () => {
    if (!event?.event_id) return;
    setStage('composing');
    setError(null);
    try {
      const body = { output_format: 'both' };
      if (subtitle.trim()) body.subtitle = subtitle.trim();
      if (hostNote.trim()) body.host_note = hostNote.trim();
      const r = await axios.post(`${API}/events/${event.event_id}/poster`, body);
      setPoster(r.data);
      setStage('ready');
      brandToast.whisper('The poster has been printed onto paper ✦');
    } catch (err) {
      setError(err.response?.data?.detail || 'Composition failed — try again.');
      setStage('error');
    }
  };

  const reset = () => {
    setStage('idle');
    setPoster(null);
    setError(null);
    setSubtitle('');
    setHostNote('');
  };

  const close = () => {
    setOpen(false);
    if (stage === 'ready') reset();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="open-event-poster-pill"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          height: 42, padding: '0 18px', borderRadius: 999,
          background: 'rgba(20,12,6,0.6)',
          border: '1px solid rgba(212,170,82,0.40)',
          color: '#FFE7A8',
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic',
          fontWeight: 500, fontSize: 12.5, letterSpacing: '0.02em',
          cursor: 'pointer',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 0 18px rgba(212,170,82,0.14)',
          transition: 'border-color 320ms ease, color 320ms ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(212,170,82,0.7)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(212,170,82,0.40)'; }}
      >
        <span aria-hidden style={{ fontSize: 13, color: '#D4AF5E' }}>✦</span>
        Compose the event poster
      </button>

      {open && (
        <div
          data-testid="event-poster-modal"
          onClick={(e) => e.target === e.currentTarget && close()}
          style={{
            position: 'fixed', inset: 0, zIndex: 220,
            background: 'rgba(10,6,3,0.82)', backdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div style={{
            width: 'min(640px, 100%)', maxHeight: '92vh', overflowY: 'auto',
            background: 'linear-gradient(180deg, #1c1108 0%, #120a05 100%)',
            border: '1px solid rgba(212,175,94,0.22)',
            borderRadius: 24,
            padding: '28px 28px 24px',
            boxShadow: '0 30px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(212,175,94,0.10)',
            color: '#FBEED0',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 18 }}>
              <div>
                <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.32em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.82)', margin: 0 }}>✦ Event Poster</p>
                <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 26, fontWeight: 500, letterSpacing: '-0.01em', margin: '8px 0 0', color: '#FFE7A8' }}>
                  {stage === 'ready' ? 'The night, printed ✦' : 'Print the whole night'}
                </h2>
                <p style={{ margin: '6px 0 0', fontStyle: 'italic', fontSize: 13, color: 'rgba(255,235,210,0.62)' }}>
                  {stage === 'ready'
                    ? 'A single page — composed to be framed.'
                    : 'An editorial spread of the moments preserved tonight. Designed to be framed, not filed.'}
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                data-testid="event-poster-close"
                style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer' }}
              ><X size={16} weight="bold" /></button>
            </div>

            {/* idle — collect optional subtitle + inscription */}
            {stage === 'idle' && (
              <div data-testid="event-poster-idle">
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.7)', marginBottom: 6 }}>
                    A line beneath the date <span style={{ textTransform: 'none', letterSpacing: 0, fontStyle: 'italic', color: 'rgba(255,235,210,0.45)' }}>(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value.slice(0, 80))}
                    placeholder="A preserved night, in moments and ink."
                    data-testid="event-poster-subtitle"
                    style={{
                      width: '100%', padding: '10px 12px',
                      background: 'rgba(20,12,6,0.7)',
                      border: '1px solid rgba(212,170,82,0.22)',
                      borderRadius: 8,
                      fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 15,
                      color: '#FFE7A8', outline: 'none',
                    }}
                  />
                </div>
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.7)', marginBottom: 6 }}>
                    A line for the cover <span style={{ textTransform: 'none', letterSpacing: 0, fontStyle: 'italic', color: 'rgba(255,235,210,0.45)' }}>(optional — defaults to the latest chapter's cover line)</span>
                  </label>
                  <input
                    type="text"
                    value={hostNote}
                    onChange={(e) => setHostNote(e.target.value.slice(0, 80))}
                    placeholder="I didn't want Year 6 to end like this."
                    data-testid="event-poster-host-note"
                    style={{
                      width: '100%', padding: '10px 12px',
                      background: 'rgba(20,12,6,0.7)',
                      border: '1px solid rgba(212,170,82,0.22)',
                      borderRadius: 8,
                      fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 15,
                      color: '#FFE7A8', outline: 'none',
                    }}
                  />
                  <p style={{ margin: '6px 2px 0', fontStyle: 'italic', fontSize: 11.5, color: 'rgba(255,235,210,0.45)' }}>
                    Leave empty to carry the chapter's inscription onto the poster.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCompose}
                  data-testid="event-poster-compose"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    height: 46, padding: '0 22px', borderRadius: 999,
                    background: 'linear-gradient(135deg, #D4AF5E 0%, #B8923F 100%)',
                    color: '#0F0B1A', border: 'none',
                    fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: 14, letterSpacing: '0.01em',
                    cursor: 'pointer',
                  }}
                ><Sparkle size={14} weight="duotone" /> Compose the poster ✦</button>
              </div>
            )}

            {/* composing — quiet typewriter beat */}
            {stage === 'composing' && (
              <div data-testid="event-poster-composing" style={{ padding: '46px 8px 40px', textAlign: 'center' }}>
                <Sparkle size={42} weight="duotone" color="#D4AF5E" style={{ animation: 'posterPulse 2.4s ease-in-out infinite', opacity: 0.85 }} />
                <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.32em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.75)', margin: '20px 0 12px' }}>✦ Pressing</p>
                <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 22, color: '#FFE7A8', margin: 0 }}>
                  The night, being printed onto paper.
                </p>
                <p style={{ fontSize: 11, color: 'rgba(255,235,210,0.42)', marginTop: 14 }}>This takes 8–12 seconds.</p>
                <style>{`
                  @keyframes posterPulse { 0%,100% { opacity: 0.4; transform: scale(0.96); } 50% { opacity: 1; transform: scale(1.05); } }
                `}</style>
              </div>
            )}

            {/* ready — frameable preview + downloads */}
            {stage === 'ready' && poster && (
              <div data-testid="event-poster-ready">
                <div style={{
                  borderRadius: 12, overflow: 'hidden', marginBottom: 18,
                  background: '#FAF6EE', border: '1px solid rgba(212,175,94,0.20)',
                  boxShadow: '0 24px 52px rgba(0,0,0,0.55)',
                }}>
                  <img
                    src={absoluteMedia(poster.png_url)}
                    alt="Event poster"
                    style={{ width: '100%', display: 'block' }}
                    data-testid="event-poster-preview-image"
                  />
                </div>
                <p style={{ fontStyle: 'italic', fontSize: 12.5, color: 'rgba(255,235,210,0.55)', margin: '0 2px 14px' }}>
                  {poster.rendered_snap_count} memories arranged on the page · {poster.snap_count} preserved tonight in total.
                </p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <a
                    href={absoluteMedia(poster.png_url)}
                    download={`event-poster-${(event?.slug || 'night').slice(0, 32)}.png`}
                    data-testid="event-poster-download-png"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 46, padding: '0 18px', borderRadius: 999, background: 'linear-gradient(135deg, #D4AF5E 0%, #B8923F 100%)', color: '#0F0B1A', textDecoration: 'none', fontWeight: 800, fontSize: 13 }}
                  ><DownloadSimple size={16} weight="bold" /> Save for printing ✦</a>
                  {poster.pdf_url && (
                    <a
                      href={absoluteMedia(poster.pdf_url)}
                      download={`event-poster-${(event?.slug || 'night').slice(0, 32)}.pdf`}
                      data-testid="event-poster-download-pdf"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 46, padding: '0 18px', borderRadius: 999, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(212,175,94,0.42)', color: '#FFE7A8', textDecoration: 'none', fontWeight: 700, fontSize: 13 }}
                    ><FilePdf size={16} weight="bold" /> Print-shop PDF</a>
                  )}
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="event-poster-another"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 46, padding: '0 18px', borderRadius: 999, background: 'transparent', border: '1px solid rgba(255,255,255,0.16)', color: 'rgba(255,235,210,0.85)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                  >Compose another</button>
                </div>
              </div>
            )}

            {/* error */}
            {stage === 'error' && (
              <div data-testid="event-poster-error" style={{ padding: '24px 4px', textAlign: 'center' }}>
                <p style={{ fontSize: 14, color: '#FFA994', margin: '0 0 12px' }}>{error}</p>
                <button
                  type="button"
                  onClick={() => setStage('idle')}
                  data-testid="event-poster-retry"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 44, padding: '0 18px', borderRadius: 999, background: 'rgba(212,175,94,0.18)', border: '1px solid rgba(212,175,94,0.4)', color: '#FFE7A8', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                >Try again</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
